/**
 * BS-2185: Sentry integration filter for the OLA browser client.
 *
 * Two suppressions, both performance-driven:
 *
 *   1. **`BrowserTracing`** - large tracing chunk that ships even when
 *      `tracesSampleRate: 0`. The build-time `bundleSizeOptimizations.
 *      excludeTracing: true` flag in `next.config.mjs` handles the
 *      tree-shake on production builds, and this runtime filter is the
 *      belt-and-suspenders complement for environments (e.g. Turbopack
 *      dev) where the define-replacement may not have fired.
 *      (Originally added by BS-2177.)
 *
 *   2. **`Breadcrumbs` DOM events** - the SDK's default `Breadcrumbs`
 *      integration wires a global `click`/`keypress`/`input` handler
 *      (via `addClickKeypressInstrumentationHandler`) which fires on
 *      every form input keystroke. Each fire walks the DOM
 *      (`htmlTreeAsString`), builds a breadcrumb object, and then runs
 *      our `beforeBreadcrumb` PII scrubber. Cumulatively this is the
 *      single largest contributor to the keystroke lag Andy reported
 *      on 2026-05-26 (BS-2185).
 *
 *      The fix is targeted: we **keep** the Breadcrumbs integration but
 *      pass `dom: false`, which preserves the console / fetch / xhr /
 *      history breadcrumb sources (all of which are valuable for error
 *      context and do NOT fire per keystroke) while disabling the DOM
 *      instrumentation that does. Tab navigations, network calls, and
 *      console output still produce breadcrumbs; typing into a form
 *      field no longer does.
 *
 * # Why a helper module instead of inline?
 *
 * The integration shape is the load-bearing piece of the keystroke fix.
 * Inlining it in `instrumentation-client.ts` makes the shape untestable
 * (the Sentry init call is a side effect at module import). Extracting it
 * here lets the unit test in `__tests__/integrations.test.ts` assert that
 * `BrowserTracing` is dropped and that `Breadcrumbs` is replaced with a
 * `{ dom: false }` instance - so a future "innocent" refactor that
 * forgets to disable DOM breadcrumbs will fail CI rather than silently
 * regress the keystroke feel.
 *
 * The contract with the SDK is intentionally narrow: we only inspect
 * `integration.name`, and we delegate to the SDK-exported
 * `breadcrumbsIntegration` factory rather than constructing a breadcrumb
 * integration ourselves. That isolates this module from minor changes
 * in the integration's internal shape across SDK versions.
 */

import { breadcrumbsIntegration } from '@sentry/nextjs';

/**
 * Sentry integration shape we care about. The real type is `Integration`
 * from `@sentry/types`; we narrow to just the `name` field to avoid
 * coupling the helper to internal SDK shape across versions.
 */
export interface SentryLikeIntegration {
  name: string;
}

/**
 * Names of integrations we explicitly drop from the default set.
 *
 * `BrowserTracing` - large tracing module; we ship `tracesSampleRate: 0`
 *   so the runtime suppression mirrors the build-time tree-shake.
 *
 * `Breadcrumbs` - the default fires on every keystroke. We replace it
 *   with a `{ dom: false }` instance below, so dropping the default is
 *   the first half of the swap.
 */
const SUPPRESSED_INTEGRATION_NAMES = new Set(['BrowserTracing', 'Breadcrumbs']);

/**
 * Build the OLA-tailored integration list from Sentry's defaults.
 *
 * Removes:
 *   - `BrowserTracing`
 *   - default `Breadcrumbs` (replaced with a `{ dom: false }` variant)
 *
 * Adds:
 *   - `breadcrumbsIntegration({ dom: false })` - keep console / fetch /
 *     xhr / history breadcrumb sources, suppress per-keystroke DOM
 *     instrumentation.
 *
 * @param defaultIntegrations - The integration array Sentry would have
 *   installed by default. Sentry passes this to the
 *   `integrations: (defaults) => …` callback at init time.
 */
export function buildIntegrations(
  defaultIntegrations: readonly SentryLikeIntegration[]
): SentryLikeIntegration[] {
  const filtered = defaultIntegrations.filter(
    (integration) => !SUPPRESSED_INTEGRATION_NAMES.has(integration.name)
  );

  // The `breadcrumbsIntegration` factory returns an SDK `Integration`. We
  // widen the inferred return type to our narrow `SentryLikeIntegration`
  // surface (which only requires a `name` field) so the helper stays
  // testable without pulling Sentry types into the test environment.
  const noDomBreadcrumbs = breadcrumbsIntegration({
    dom: false,
  }) as unknown as SentryLikeIntegration;

  return [...filtered, noDomBreadcrumbs];
}
